import React, { useEffect, useRef, useState } from "react";
import { Mail, Phone, BookOpen, X, Plus, Instagram, Twitter, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * FloatingMenu - small heritage-styled action menu pinned in the right
 * corner, sitting just above the WhatsApp button. Auto-opens for 2s when
 * the user lands, then closes itself. Clicking the toggle re-opens it.
 *
 * Contents:
 *   • Email Export Desk
 *   • Call (mobile)
 *   • Social profiles
 *   • Read the Catalogue (route)
 */

const AUTO_OPEN_MS = 2000;
const AUTO_OPEN_KEY = "vilora-floating-menu-auto-shown";
const INSTAGRAM_URL = "https://www.instagram.com/viloraimpex?igsh=Z3VzOXY0ZHYzeWJr";
const X_URL = "https://x.com/viloraimpex";

export default function FloatingMenu() {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Auto-open once per session for 2 seconds, then close
  useEffect(() => {
    if (window.sessionStorage.getItem(AUTO_OPEN_KEY) === "1") return undefined;
    const openTimer = window.setTimeout(() => setOpen(true), 350);
    const closeTimer = window.setTimeout(() => {
      setOpen(false);
      window.sessionStorage.setItem(AUTO_OPEN_KEY, "1");
    }, 350 + AUTO_OPEN_MS);
    return () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    };
  }, []);

  // Click outside / Escape closes when manually opened
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      className="floating-menu"
      data-testid="floating-menu-wrapper"
    >
      {/* Popover - three action rows */}
      <div
        className={`floating-menu-panel ${open ? "is-open" : ""}`}
        role="dialog"
        aria-label="Export desk quick actions"
        aria-hidden={!open}
      >
        <div className="floating-menu-head">
          <span className="floating-menu-folio">EXPORT DESK</span>
          <button
            type="button"
            className="floating-menu-close"
            aria-label="Close"
            onClick={() => setOpen(false)}
            tabIndex={open ? 0 : -1}
          >
            <X size={12} strokeWidth={1.7} />
          </button>
        </div>

        <a
          href="mailto:exports@viloraimpex.com"
          className="floating-menu-item"
          tabIndex={open ? 0 : -1}
        >
          <span className="floating-menu-item-icon">
            <Mail size={16} strokeWidth={1.6} />
          </span>
          <span className="floating-menu-item-stack">
            <span className="floating-menu-item-label">Email</span>
            <span className="floating-menu-item-value">exports@viloraimpex.com</span>
          </span>
        </a>

        <a
          href="tel:+918669999901"
          className="floating-menu-item"
          tabIndex={open ? 0 : -1}
        >
          <span className="floating-menu-item-icon">
            <Phone size={16} strokeWidth={1.6} />
          </span>
          <span className="floating-menu-item-stack">
            <span className="floating-menu-item-label">Call</span>
            <span className="floating-menu-item-value">+91 86699 99901</span>
          </span>
        </a>

        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="floating-menu-item"
          tabIndex={open ? 0 : -1}
        >
          <span className="floating-menu-item-icon">
            <Instagram size={16} strokeWidth={1.6} />
          </span>
          <span className="floating-menu-item-stack">
            <span className="floating-menu-item-label">Instagram</span>
            <span className="floating-menu-item-value floating-menu-item-value--clickable">
              @viloraimpex
              <ExternalLink size={11} strokeWidth={1.7} />
            </span>
          </span>
        </a>

        <a
          href={X_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="floating-menu-item"
          tabIndex={open ? 0 : -1}
        >
          <span className="floating-menu-item-icon">
            <Twitter size={16} strokeWidth={1.6} />
          </span>
          <span className="floating-menu-item-stack">
            <span className="floating-menu-item-label">X</span>
            <span className="floating-menu-item-value floating-menu-item-value--clickable">
              @viloraimpex
              <ExternalLink size={11} strokeWidth={1.7} />
            </span>
          </span>
        </a>

        <Link
          to="/catalogue"
          onClick={() => {
            setOpen(false);
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
          }}
          className="floating-menu-item floating-menu-item--cta"
          tabIndex={open ? 0 : -1}
        >
          <span className="floating-menu-item-icon">
            <BookOpen size={16} strokeWidth={1.6} />
          </span>
          <span className="floating-menu-item-stack">
            <span className="floating-menu-item-label">Read</span>
            <span className="floating-menu-item-value">View Catalogue →</span>
          </span>
        </Link>
      </div>

      {/* Toggle - same circle size as WhatsApp button */}
      <button
        type="button"
        className={`floating-menu-toggle ${open ? "is-open" : ""}`}
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="floating-menu-toggle-icon">
          {open ? <X size={20} strokeWidth={1.8} /> : <Plus size={20} strokeWidth={1.8} />}
        </span>
      </button>
    </div>
  );
}
